import { CnCodeSelector } from '@/shared/components/CnCodeSelector'
import useI18n from '@/shared/hooks/usei18n'
import {
  CheckboxGroup,
  GenericCombobox,
  InputGroup,
  NON_EU_COUNTRIES,
  NumericInputGroup
} from '@/common'
import { parsePeriodId } from '@artem-cbam-declarants/shared'
import { CbamSimulationGoodFormData } from './CbamSimulationGoodForm.schema'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useCallback, useMemo } from 'react'
import { aggregatedGoodCategoriesByGoodFamilies } from '@/common'
import { orderBy } from 'es-toolkit'
import { Installation } from '@/features/suppliers/hooks/installations'
import { DeclarantDatePickerSingle } from '@/shared/components'

export const initialState: CbamSimulationGoodFormData = {
  aggregatedGoodsCategory: undefined,
  cnCode: '',
  country: '',
  description: '',
  netMassInTonnes: 0,
  isUsingDefaultEmissions: true,
  supplierEmissionsReportId: undefined,
  importDate: ''
}

export default function CbamSimulationGoodForm({
  data,
  onChange,
  supplierEmissionsReports,
  installations,
  simulationYear
}: {
  data: CbamSimulationGoodFormData
  onChange: (data: CbamSimulationGoodFormData) => void
  supplierEmissionsReports: SupplierEmissionsReport[]
  installations: Installation[]
  simulationYear: number
}) {
  const { t, dateLongFormatterMonthYear } = useI18n()
  const hasEmissionReports = useMemo(() => {
    return supplierEmissionsReports.length > 0
  }, [supplierEmissionsReports])

  const {
    aggregatedGoodsCategory,
    cnCode,
    country,
    description,
    netMassInTonnes,
    isUsingDefaultEmissions,
    supplierEmissionsReportId,
    importDate
  } = data

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  const aggregatedGoodsOptions = Object.values(aggregatedGoodCategoriesByGoodFamilies).flatMap(
    aggregatedGoods =>
      aggregatedGoods.map(aggregatedGood => ({
        label: t(`common:aggregatedGoodCategories.${aggregatedGood.value}`),
        value: aggregatedGood.value
      }))
  )

  const supplierEmissionsReportsOptions = useMemo(
    () =>
      supplierEmissionsReports.map(report => {
        const period = parsePeriodId(report.periodId)
        const periodLabel = `${dateLongFormatterMonthYear(period.startDate)} - ${dateLongFormatterMonthYear(period.endDate)}`
        return {
          value: report.id,
          label: `${report.goods[0]?.goodsDescription || 'Unknown goods'} - ${report.goods[0]?.cnCode || 'N/A'} (${periodLabel})`
        }
      }),
    [supplierEmissionsReports, dateLongFormatterMonthYear]
  )

  const onAggregatedGoodsCategoryChange = useCallback(
    (value: string) => {
      onChange({
        ...data,
        cnCode: '',
        description: '',
        aggregatedGoodsCategory: value || ''
      })
    },
    [onChange, data]
  )

  const onSupplierEmissionsReportChange = useCallback(
    (value: string) => {
      const supplierEmissionsReport = supplierEmissionsReports.find(report => report.id === value)
      const installation = installations.find(
        installation => installation.id === supplierEmissionsReport?.installationId
      )
      onChange({
        ...data,
        supplierEmissionsReportId: value,
        country: installation?.country || '',
        cnCode: supplierEmissionsReport?.goods[0]?.cnCode || '',
        description: supplierEmissionsReport?.goods[0]?.goodsDescription || ''
      })
    },
    [onChange, data, supplierEmissionsReports, installations]
  )

  const onIsUsingDefaultEmissionsChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...data,
        cnCode: '',
        aggregatedGoodsCategory: undefined,
        description: '',
        country: '',
        supplierEmissionsReportId: undefined,
        isUsingDefaultEmissions: checked
      })
    },
    [onChange, data]
  )

  const onCnCodeUpdate = useCallback(
    (value: string) => {
      onChange({
        ...data,
        cnCode: value
      })
    },
    [onChange, data]
  )

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-7 gap-4">
        <div className="col-span-3">
          {isUsingDefaultEmissions ? (
            <GenericCombobox
              label={t('cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.country')}
              value={
                country
                  ? { value: country, label: t(`common:countriesByIsoAlpha2.${country}`) }
                  : null
              }
              onChange={value => onChange({ ...data, country: value?.value || '' })}
              items={countryOptions}
            />
          ) : (
            <GenericCombobox
              label={t(
                'cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.supplierEmissionsReport'
              )}
              required={!isUsingDefaultEmissions}
              disabled={isUsingDefaultEmissions}
              items={supplierEmissionsReportsOptions}
              value={
                supplierEmissionsReportId
                  ? {
                      value: supplierEmissionsReportId,
                      label:
                        supplierEmissionsReportsOptions.find(
                          option => option.value === supplierEmissionsReportId
                        )?.label || ''
                    }
                  : null
              }
              onChange={value => onSupplierEmissionsReportChange(value?.value || '')}
            />
          )}
        </div>
        <div className="col-span-2">
          <DeclarantDatePickerSingle
            id="importDate"
            startMonth={new Date(simulationYear, 0, 1)}
            endMonth={new Date(simulationYear, 11, 31)}
            label={t('cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.importDate')}
            required
            date={importDate || ''}
            onChange={value => onChange({ ...data, importDate: value })}
          />
        </div>
        <div className="col-span-2">
          <NumericInputGroup
            label={t('cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.quantity')}
            value={netMassInTonnes.toString()}
            required
            suffix={'t'}
            numericFormat={{
              allowNegative: false,
              decimalScale: 6
            }}
            onChange={evt => onChange({ ...data, netMassInTonnes: Number(evt.target.value) })}
          />
        </div>
      </div>
      {isUsingDefaultEmissions && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <GenericCombobox
              disabled={!isUsingDefaultEmissions}
              label={t(
                'cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.aggregatedGoodsCategory'
              )}
              items={aggregatedGoodsOptions}
              value={
                aggregatedGoodsOptions.find(option => option.value === aggregatedGoodsCategory) ??
                null
              }
              onChange={value => onAggregatedGoodsCategoryChange(value?.value || '')}
            />
            <InputGroup
              disabled={!isUsingDefaultEmissions}
              label={t(
                'cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.goodsDescription'
              )}
              value={description ?? ''}
              onChange={value => onChange({ ...data, description: value.target.value })}
            />
          </div>
          <CnCodeSelector
            disabled={!isUsingDefaultEmissions}
            required
            label={t('cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.cnCode')}
            aggregatedGoodsCategories={
              aggregatedGoodsCategory ? [aggregatedGoodsCategory] : undefined
            }
            cnCode={cnCode}
            onCnCodeUpdate={onCnCodeUpdate}
          />
        </>
      )}
      <CheckboxGroup
        disabled={!hasEmissionReports}
        id="isUsingDefaultEmissions"
        label={t(
          'cbamSimulations:cbamSimulationDetails.simulationGoodForm.fields.isUsingDefaultEmissions'
        )}
        checked={isUsingDefaultEmissions}
        onChange={onIsUsingDefaultEmissionsChange}
      />
    </div>
  )
}
